import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { MdEdit, MdAddCircle } from "react-icons/md";
import AppDataGrid from "../../../components/common/AppDataGrid";

function AddServiceMaster() {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const routeLocation = useLocation();

  const [isSaving, setIsSaving] = useState(false);

  const [serviceAccounts, setServiceAccounts] = useState([]);
  const [tdsAccounts, setTdsAccounts] = useState([]);
  const [codeTypes, setCodeTypes] = useState([]);
  const [codesMap, setCodesMap] = useState({});

  const [accountCode, setAccountCode] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [isTdsMandatory, setIsTdsMandatory] = useState("N");
  const [tdsAccountCode, setTdsAccountCode] = useState(null);

  const [lineCodeType, setLineCodeType] = useState(null);
  const [lineCode, setLineCode] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);

  const [lines, setLines] = useState([]);

  // keep original effective date from the record (for edit mode)
  const [originalEffectiveDate, setOriginalEffectiveDate] = useState("");

  // -------- Helpers --------
  const normalizeYN = (val, defaultVal = "N") => {
    if (val === undefined || val === null || val === "") return defaultVal;

    const v =
      typeof val === "string"
        ? val.trim().toUpperCase()
        : typeof val === "boolean"
        ? (val ? "Y" : "N")
        : String(val).trim().toUpperCase();

    if (["Y", "YES", "1", "TRUE"].includes(v)) return "Y";
    if (["N", "NO", "0", "FALSE"].includes(v)) return "N";

    return defaultVal;
  };

  const toInputDate = (value) => {
    if (!value) return "";
    try {
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }

      const d = new Date(value);
      if (Number.isNaN(d.getTime())) {
        if (typeof value === "string" && value.length >= 10) {
          return value.slice(0, 10);
        }
        return "";
      }

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      if (typeof value === "string" && value.length >= 10) {
        return value.slice(0, 10);
      }
      return "";
    }
  };

  const makeAccountLabel = (row) => {
    if (!row) return "";
    return row.acc_code
      ? `${row.acc_code} ${row.name_on_account}`
      : row.name_on_account;
  };

  const fetchServiceAccountCodes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/account_master/by_accounttype/", {
        accounttype_id: 2,
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      setServiceAccounts(data);
    } catch (e) {
      toast.error("Failed to load service account codes");
    }
  };

  const fetchTdsAccountCodes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/account_master/by_accounttype/", {
        accounttype_id: 1,
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      setTdsAccounts(data);
    } catch (e) {
      toast.error("Failed to load TDS account codes");
    }
  };

  const fetchCodeTypes = async () => {
    try {
      const resp = await axios.get(baseUrl + "/code_type/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setCodeTypes(data);
    } catch (e) {
      toast.error("Failed to load code types");
    }
  };

  const fetchCodesForType = async (typeValue) => {
    if (!typeValue) return;
    try {
      const resp = await axios.post(baseUrl + "/hsn_sac/by_codetype/", {
        code_type: typeValue, // backend expects name like "HSN"
      });
      const arr = Array.isArray(resp.data) ? resp.data : [];
      setCodesMap((m) => ({ ...m, [typeValue]: arr }));
    } catch (e) {
      toast.error("Failed to load codes");
    }
  };

  // Load masters
  useEffect(() => {
    fetchServiceAccountCodes();
    fetchTdsAccountCodes();
    fetchCodeTypes();
  }, []);

  useEffect(() => {
    const svc = routeLocation.state?.ServiceData;
    if (!svc) return;

    // Header: account code
    if (svc.account_master_id) {
      const found = serviceAccounts.find(
        (x) => x.account_master_id === svc.account_master_id
      );
      if (found) {
        setAccountCode({
          value: found.account_master_id,
          label: makeAccountLabel(found),
        });
      }
    } else if (svc.account_code) {
      const found = serviceAccounts.find((x) => x.acc_code === svc.account_code);
      if (found) {
        setAccountCode({
          value: found.account_master_id,
          label: makeAccountLabel(found),
        });
      }
    }

    // Header: effective date
    if (svc.effective_date) {
      const eff = toInputDate(svc.effective_date);
      setEffectiveDate(eff);
      setOriginalEffectiveDate(eff);
    }

    // ---------- Is TDS Mandatory (header) ----------
    // Prefer header fields, but if they are missing/blank
    // infer from lines (any line with TDS => header "Y")
    let tdsFromHeader =
      svc.is_tds_mandatory !== undefined && svc.is_tds_mandatory !== null
        ? svc.is_tds_mandatory
        : svc.is_tds !== undefined && svc.is_tds !== null
        ? svc.is_tds
        : null;

    let headerTds = normalizeYN(tdsFromHeader, "N");

    if (
      (!tdsFromHeader || String(tdsFromHeader).trim() === "") &&
      Array.isArray(svc.lines)
    ) {
      const lineHasTds = svc.lines.some(
        (l) =>
          normalizeYN(l?.is_tds, "N") === "Y" ||
          (l?.tds_account_master_id &&
            Number(l.tds_account_master_id) !== 0)
      );
      if (lineHasTds) {
        headerTds = "Y";
      }
    }

    setIsTdsMandatory(headerTds); // "Y" or "N"

    // ---------- Prefill TDS Account Code ----------
    let headerTdsAccountId = svc.tds_account_master_id || null;
    if (!headerTdsAccountId && Array.isArray(svc.lines)) {
      const lineWithTds = svc.lines.find(
        (l) => l.tds_account_master_id && Number(l.tds_account_master_id) !== 0
      );
      if (lineWithTds) {
        headerTdsAccountId = lineWithTds.tds_account_master_id;
      }
    }

    if (headerTdsAccountId && tdsAccounts.length > 0) {
      const tdsAccRow = tdsAccounts.find(
        (x) => x.account_master_id === headerTdsAccountId
      );
      if (tdsAccRow) {
        setTdsAccountCode({
          value: tdsAccRow.account_master_id,
          label: makeAccountLabel(tdsAccRow),
        });
      }
    }

    // ---------- Lines ----------
    if (Array.isArray(svc.lines)) {
      setLines(
        svc.lines.map((l, idx) => {
          const isNewShape =
            l.code_type_id !== undefined ||
            l.hsn_sac_master_id !== undefined ||
            l.code_type_name !== undefined;

          const baseLineDate = l.effective_date || svc.effective_date || "";
          const effDate = toInputDate(baseLineDate);

          const rawIsTds = isNewShape
            ? l.is_tds
            : l.is_tds ?? headerTds ?? "N";
          const isTds = normalizeYN(rawIsTds, headerTds);

          const codeTypeName = isNewShape ? l.code_type_name || "" : l.code_type;
          const codeTypeId = isNewShape ? l.code_type_id || 0 : l.code_type_id || 0;

          const codeValue = isNewShape ? l.hsn_sac_master_id : l.code;
          const codeLabel = isNewShape ? l.hsn_sac_code : l.code_label || l.code;

          const tdsAccId = l.tds_account_master_id || null;
          const tdsLabel = isNewShape ? l.tdsacc_code || "" : l.tds_code_label || "";

          return {
            id: l.service_master_detail_id || idx + 1,
            service_master_detail_id: l.service_master_detail_id || 0,
            effective_date: effDate,
            is_tds: isTds,
            code_type_name: codeTypeName,
            code_type_id: codeTypeId,
            hsn_sac_master_id: codeValue,
            hsn_sac_code: codeLabel,
            tds_account_master_id: tdsAccId,
            tds_code_label: tdsLabel,
          };
        })
      );
    }
  }, [routeLocation.state, serviceAccounts, tdsAccounts]);

  const serviceAccountOptions = useMemo(
    () =>
      (serviceAccounts || []).map((x) => ({
        value: x.account_master_id,
        label: makeAccountLabel(x),
      })),
    [serviceAccounts]
  );

  const tdsAccountOptions = useMemo(
    () =>
      (tdsAccounts || []).map((x) => ({
        value: x.account_master_id,
        label: makeAccountLabel(x),
      })),
    [tdsAccounts]
  );

  const codeTypeOptions = useMemo(
    () =>
      (codeTypes || []).map((x) => ({
        value: x.code_type_name,
        label: x.code_type_name,
        code_type_id: x.code_type_id,
      })),
    [codeTypes]
  );

  const lineCodeOptions = useMemo(() => {
    const t = lineCodeType?.value; // name like "HSN"
    const raw = t ? codesMap[t] || [] : [];
    return raw.map((x) => ({
      value: x.hsn_sac_master_id,
      label: x.hsn_sac_code,
    }));
  }, [lineCodeType, codesMap]);

  useEffect(() => {
    if (lineCodeType?.value && !codesMap[lineCodeType.value]) {
      fetchCodesForType(lineCodeType.value);
    }
  }, [lineCodeType]);

  const clearLineEditor = () => {
    setLineCodeType(null);
    setLineCode(null);
    setEditIndex(-1);
  };

  const onAddLine = () => {
    if (!effectiveDate) return toast.error("Effective Date is required");
    if (!lineCodeType?.value) return toast.error("Select a Code Type");
    if (!lineCode?.value) return toast.error("Select a Code");
    if (isTdsMandatory === "Y" && !tdsAccountCode?.value)
      return toast.error("Select TDS Code");

    const newLine = {
      id: editIndex >= 0 ? lines[editIndex].id : Date.now(),
      service_master_detail_id:
        editIndex >= 0 ? lines[editIndex].service_master_detail_id || 0 : 0,

      effective_date: effectiveDate,
      is_tds: isTdsMandatory,

      code_type_name: lineCodeType.value,
      code_type_id: lineCodeType.code_type_id || 0,

      hsn_sac_master_id: lineCode.value,
      hsn_sac_code: lineCode.label,

      tds_account_master_id:
        isTdsMandatory === "Y" ? tdsAccountCode?.value || null : null,
      tds_code_label: isTdsMandatory === "Y" ? tdsAccountCode?.label || "" : "",
    };

    if (editIndex >= 0) {
      const copy = [...lines];
      copy[editIndex] = newLine;
      setLines(copy);
      toast.success("Line updated");
    } else {
      setLines((prev) => [...prev, newLine]);
      toast.success("Line added");
    }
    clearLineEditor();
  };

  const onEditRow = (rowId) => {
    const idx = lines.findIndex((r) => r.id === rowId);
    if (idx < 0) return;

    const r = lines[idx];

    if (r.effective_date) {
      setEffectiveDate(toInputDate(r.effective_date));
    }

    const rowTdsFlag = normalizeYN(r.is_tds, "N");
    setIsTdsMandatory(rowTdsFlag);

    if (rowTdsFlag === "Y" && r.tds_account_master_id) {
      const tdsOpt =
        tdsAccountOptions.find(
          (o) => o.value === r.tds_account_master_id
        ) || {
          value: r.tds_account_master_id,
          label: r.tds_code_label || "TDS",
        };

      setTdsAccountCode(tdsOpt);
    } else {
      setTdsAccountCode(null);
    }

    const ct =
      codeTypeOptions.find(
        (o) => o.value === r.code_type_name || o.code_type_id === r.code_type_id
      ) || {
        value: r.code_type_name,
        label: r.code_type_name || String(r.code_type_id || ""),
        code_type_id: r.code_type_id || 0,
      };

    setLineCodeType(ct);

    if (!codesMap[ct.value]) {
      fetchCodesForType(ct.value).then(() => {
        setLineCode({
          value: r.hsn_sac_master_id,
          label: r.hsn_sac_code || "",
        });
      });
    } else {
      setLineCode({
        value: r.hsn_sac_master_id,
        label: r.hsn_sac_code || "",
      });
    }

    setEditIndex(idx);
  };

  const columns = useMemo(
    () => [
      {
        field: "effective_date",
        headerName: "Effective Date",
        flex: 1,
        align: "center",
        headerAlign: "center",
        valueFormatter: (params) => {
          const value = params?.value;
          if (!value) return "";
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return value;
        },
      },
      {
        field: "is_tds",
        headerName: "Is TDS",
        flex: 0.6,
        align: "center",
        headerAlign: "center",
        valueFormatter: (p) => (p?.value === "Y" ? "Yes" : "No"),
      },
      {
        field: "tds_code_label",
        headerName: "TDS Code",
        flex: 1,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "code_type_name",
        headerName: "Code Type",
        flex: 1,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "hsn_sac_code",
        headerName: "Code",
        flex: 1,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.8,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <div className="tableActions">
            <span
              className="commonActionIcons"
              onClick={() => onEditRow(params.row.id)}
            >
              <MdEdit />
            </span>
          </div>
        ),
      },
    ],
    [lines]
  );

  const validateEffectiveDate = async () => {
    try {
      const resp = await axios.post(
        baseUrl + "/service_master/check_effective_date/",
        {
          flag: "SER",
        }
      );

      const d = resp.data;
      const last = d.effective_date;
      if (last) {
        const lastDate = last.slice(0, 10);
        if (effectiveDate && effectiveDate < lastDate) {
          toast.error(
            `Effective Date should be greater than or equal to last effective date ${lastDate}`
          );
          return false;
        }
      }
    } catch (e) {
      toast.error("Error validating effective date");
      return false;
    }
    return true;
  };

  const checkDuplicationEffectiveDate = async (overrideTdsId) => {
    try {
      const url = baseUrl + "/service_master/check_duplication_effective_date/";

      if (!accountCode?.value) {
        return true;
      }

      const tdsAccountId =
        overrideTdsId !== undefined
          ? overrideTdsId
          : isTdsMandatory === "Y"
          ? tdsAccountCode?.value || 0
          : 0;

      const payload = {
        code_type_id: accountCode.value,
        hsn_sac_master_id: 0,
        gst_type_id: userObj.school_id || 0,
        flag: "SERM",
        effective_date: effectiveDate,
        id: routeLocation.state?.ServiceData?.service_master_id || 0,
        tds_account_master_id: tdsAccountId,
      };

      const resp = await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const d = resp.data || {};
      const duplicate = Number(d.result) > 0;

      if (duplicate) {
        toast.error(
          d.message ||
            "Record already exists with the same Account Code, Effective Date and TDS Code."
        );
        return false;
      }

      return true;
    } catch (e) {
      console.error("Duplication check error:", e?.response || e);
      if (e?.response?.data) {
        console.error("Backend says:", e.response.data);
      }
      toast.error("Error checking duplicate effective date");
      return false;
    }
  };

  // 🔹 Handle TDS Code selection – block duplicates in current grid + DB
  const handleTdsCodeChange = async (opt) => {
    if (!opt) {
      setTdsAccountCode(null);
      return;
    }

    if (!accountCode?.value) {
      toast.error("Select Account Code before TDS Code");
      setTdsAccountCode(null);
      return;
    }
    if (!effectiveDate) {
      toast.error("Select Effective Date before TDS Code");
      setTdsAccountCode(null);
      return;
    }

    // Front-end duplicate check inside current grid (same AccCode + EffDate + TDS)
    const selectedTdsId = opt.value;
    const duplicateInLines = lines.some((line, idx) => {
      // ignore the row we're currently editing (if any)
      if (editIndex >= 0 && idx === editIndex) return false;

      return (
        normalizeYN(line.is_tds, "N") === "Y" &&
        (line.effective_date || "") === (effectiveDate || "") &&
        (line.tds_account_master_id || 0) === selectedTdsId
      );
    });

    if (duplicateInLines) {
      toast.error(
        "Record already exists for this Account Code, Effective Date and TDS Code in the current list."
      );
      setTdsAccountCode(null);
      return;
    }

    // If no front-end duplicate, set it and (optionally) check DB-level duplicate
    setTdsAccountCode(opt);

    if (isTdsMandatory === "Y") {
      const ok = await checkDuplicationEffectiveDate(selectedTdsId);
      if (!ok) {
        // DB duplicate: clear selection as well
        setTdsAccountCode(null);
        return;
      }
      toast.success(`TDS Code selected: ${opt.label}`);
    } else {
      toast.success(`TDS Code selected: ${opt.label}`);
    }
  };

  const onSave = async () => {
    if (!accountCode?.value) return toast.error("Account Code is required");
    if (!effectiveDate) return toast.error("Effective Date is required");
    if (lines.length === 0) return toast.error("Add at least one line");
    if (isTdsMandatory === "Y" && !tdsAccountCode?.value) {
      return toast.error("TDS Code is required when TDS is mandatory");
    }

    const isEdit = !!routeLocation.state?.ServiceData;

    setIsSaving(true);
    try {
      if (!isEdit) {
        const isDateOk = await validateEffectiveDate();
        if (!isDateOk) {
          setIsSaving(false);
          return;
        }
      }

      let runDuplicateCheck = true;
      if (isEdit) {
        const original = (originalEffectiveDate || "").slice(0, 10);
        const current = (effectiveDate || "").slice(0, 10);

        const originalTdsId =
          routeLocation.state?.ServiceData?.tds_account_master_id || 0;

        const currentTdsId =
          isTdsMandatory === "Y" ? tdsAccountCode?.value || 0 : 0;

        runDuplicateCheck = original !== current || originalTdsId !== currentTdsId;
      }

      if (runDuplicateCheck) {
        const isNotDuplicate = await checkDuplicationEffectiveDate();
        if (!isNotDuplicate) {
          setIsSaving(false);
          return;
        }
      }

      const createdBy =
        userObj.email_id || userObj.user_name || userObj.username || "system";

      const payload = {
        action: isEdit ? "UPDATE" : "CREATE",
        school_id: String(userObj.school_id),
        service_master_id:
          routeLocation.state?.ServiceData?.service_master_id || 0,
        created_by: createdBy,

        effective_date: effectiveDate,
        is_tds: isTdsMandatory,

        header: {
          account_code: accountCode.value,
          effective_date: effectiveDate,
          is_tds_mandatory: isTdsMandatory,
          tds_account_master_id:
            isTdsMandatory === "Y" ? tdsAccountCode?.value || null : null,
        },

        lines: lines.map((l) => ({
          service_master_detail_id: l.service_master_detail_id || 0,
          effective_date: l.effective_date,
          is_tds: l.is_tds,
          code_type_id: l.code_type_id || 0,
          hsn_sac_master_id: l.hsn_sac_master_id || 0,
          tds_account_master_id: l.tds_account_master_id || null,
        })),
      };

      await axios.post(baseUrl + "/servicemaster/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success("Service saved");
    } catch (e) {
      console.error("Save error:", e?.response || e);
      toast.error("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const onResetForm = () => {
    setAccountCode(null);
    setEffectiveDate("");
    setIsTdsMandatory("N");
    setTdsAccountCode(null);
    setLineCodeType(null);
    setLineCode(null);
    setEditIndex(-1);
    setLines([]);
    setOriginalEffectiveDate("");
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <Row>
              <Col xs={12}>
                <h6 className="commonSectionTitle">Add Service</h6>
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} lg={4} xxl={3}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>
                      Account Code <span className="requiredStar">*</span>
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      placeholder="Select Account Code"
                      isSearchable
                      isClearable
                      value={accountCode}
                      onChange={(opt) => setAccountCode(opt)}
                      options={serviceAccountOptions}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12} md={6} lg={4} xxl={3}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>
                      Effective Date <span className="requiredStar">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12} md={6} lg={4} xxl={3}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>
                      Is TDS Mandatory <span className="requiredStar">*</span>
                    </Form.Label>
                    <Form.Select
                      value={isTdsMandatory}
                      onChange={(e) => {
                        const val = e.target.value;
                        setIsTdsMandatory(val);
                        if (val === "N") {
                          setTdsAccountCode(null);
                        }
                      }}
                    >
                      <option value="">Select</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>

              {isTdsMandatory === "Y" && (
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        TDS Code <span className="requiredStar">*</span>
                      </Form.Label>
                      <Select
                        classNamePrefix="react-select"
                        placeholder="Select TDS Code"
                        isSearchable
                        isClearable
                        value={tdsAccountCode}
                        onChange={handleTdsCodeChange}
                        options={tdsAccountOptions}
                      />
                    </Form.Group>
                  </div>
                </Col>
              )}

              <Col xs={12} md={6} lg={4} xxl={3}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>
                      Code Type <span className="requiredStar">*</span>
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      placeholder="Select Code Type"
                      isSearchable
                      isClearable
                      value={lineCodeType}
                      onChange={(opt) => {
                        setLineCodeType(opt);
                        setLineCode(null);
                      }}
                      options={codeTypeOptions}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12} md={6} lg={4} xxl={3}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>
                      Code <span className="requiredStar">*</span>
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      placeholder="Select"
                      isSearchable
                      isClearable
                      value={lineCode}
                      onChange={(opt) => setLineCode(opt)}
                      options={lineCodeOptions}
                      isDisabled={!lineCodeType}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12} md={6} lg={4} xxl={3}>
                <div className="commonInput d-flex align-items-end">
                  <Button
                    style={{ marginTop: 27 }}
                    variant="primary"
                    className="btn-success primaryBtn"
                    onClick={onAddLine}
                  >
                    {editIndex >= 0 ? "Update" : "Add"}
                  </Button>
                </div>
              </Col>
            </Row>

            <div className="mt-3">
              <AppDataGrid
                rows={lines}
                columns={columns}
                getRowId={(r) => r.id}
                pageSizeOptions={[5, 10, 20]}
                density="compact"
                maxHeight={300}
                showToolbar={false}
              />
            </div>

            <div className="d-flex justify-content-between mt-3">
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  className="btn-info clearBtn"
                  onClick={onResetForm}
                >
                  Reset
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  className="btn-danger secondaryBtn me-2"
                  onClick={() => navigate("/finance/services")}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={isSaving}
                  variant="primary"
                  className="btn-success primaryBtn"
                  onClick={onSave}
                >
                  Submit
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default AddServiceMaster;
